import React, { useEffect, useState } from 'react';
import { FileText, Download, Calendar, Newspaper, Loader2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import { useTranslation } from '../i18nContext';
import { api } from '../lib/api';

const accentMap = {
  cmic: { bg: 'bg-cmic-50', text: 'text-cmic-700', border: 'border-cmic-200', cardBorder: 'hover:border-cmic-300', iconBg: 'bg-cmic-100', iconText: 'text-cmic-600', tabActive: 'bg-cmic-600 text-white' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', cardBorder: 'hover:border-teal-300', iconBg: 'bg-teal-100', iconText: 'text-teal-600', tabActive: 'bg-teal-600 text-white' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', cardBorder: 'hover:border-amber-300', iconBg: 'bg-amber-100', iconText: 'text-amber-600', tabActive: 'bg-amber-600 text-white' },
};

function formatMonth(dateStr, locale) {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString(locale === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'long' });
}

export default function FlashPage() {
  const t = useTranslation();
  const locale = t.locale;
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    api.get('/flash/bulletins')
      .then((r) => {
        const secs = r.data?.sections || [];
        setSections(secs);
        if (secs.length > 0) setActiveSection(secs[0].id);
      })
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <section className="py-32 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={32} /></section>;
  }

  const currentSection = sections.find((s) => s.id === activeSection) || sections[0];
  const styles = accentMap[currentSection?.accent || 'cmic'];

  return (
    <>
      <PageHero
        label={locale === 'es' ? 'Publicaciones' : 'Publications'}
        title={locale === 'es' ? 'Boletín Flash' : 'Flash Newsletter'}
        subtitle={locale === 'es' ? 'Publicación esporádica recurrente del Colegio Mexicano de Intérpretes de Conferencias' : 'Recurring sporadic publication of the Mexican College of Conference Interpreters'}
      />
      <section className="py-16 sm:py-20 bg-white" data-testid="flash-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-cmic-100 flex items-center justify-center">
              <Newspaper size={22} className="text-cmic-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-charcoal-800">{locale === 'es' ? 'Archivo de ediciones' : 'Issue Archive'}</h2>
              <p className="text-charcoal-400 text-sm">{locale === 'es' ? 'Consulta y descarga las ediciones del boletín Flash.' : 'Browse and download Flash newsletter issues.'}</p>
            </div>
          </div>

          {sections.length === 0 && (
            <p className="py-12 text-center text-charcoal-500">{locale === 'es' ? 'Aún no hay ediciones disponibles.' : 'No issues available yet.'}</p>
          )}

          {sections.length > 0 && currentSection && (
            <>
              <div className="flex flex-wrap gap-2 mb-8">
                {sections.map((section) => {
                  const sStyles = accentMap[section.accent || 'cmic'];
                  const isActive = activeSection === section.id;
                  return (
                    <button key={section.id} onClick={() => setActiveSection(section.id)}
                      data-testid={`flash-tab-${section.id}`}
                      className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${isActive ? sStyles.tabActive + ' shadow-sm' : 'bg-charcoal-100 text-charcoal-600 hover:bg-charcoal-200'}`}>
                      {section.label?.[locale] || section.label?.es || section.id}
                    </button>
                  );
                })}
              </div>

              <div className={`rounded-2xl border ${styles.border} ${styles.bg} p-6 mb-8`}>
                <p className={`text-sm font-medium ${styles.text}`}>{currentSection.description?.[locale] || currentSection.description?.es || ''}</p>
                <p className="text-charcoal-400 text-xs mt-1">{(currentSection.issues || []).length} {locale === 'es' ? 'ediciones' : 'issues'}</p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {(currentSection.issues || []).map((issue) => (
                  <a key={issue.file} href={absoluteUrl(issue.file)} download
                    className={`group bg-white rounded-xl border border-charcoal-100 ${styles.cardBorder} p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex flex-col`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-11 h-11 rounded-xl ${styles.iconBg} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                        <FileText size={20} className={styles.iconText} />
                      </div>
                      <Download size={16} className="text-charcoal-300 group-hover:text-charcoal-500 transition-colors" />
                    </div>
                    <h3 className="font-bold text-charcoal-800 text-sm mb-1 group-hover:text-charcoal-900">{issue.title}</h3>
                    {issue.description && (<p className="text-xs text-charcoal-500 mb-2">{issue.description}</p>)}
                    {issue.date && (
                      <div className="flex items-center gap-1.5 mt-auto pt-3">
                        <Calendar size={12} className="text-charcoal-400" />
                        <span className="text-xs text-charcoal-400 capitalize">{formatMonth(issue.date, locale)}</span>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
